'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/shared/components/Button';
import VideoCarousel from '@/shared/components/VideoCarousel';
import { useAnalytics } from '@/core/analytics';

export default function SecurityPage() {
	const analytics = useAnalytics();

	useEffect(() => {
		analytics.logEvent('security_page_visited');
	}, []);

	return (
		<div className="min-h-screen overflow-hidden">
			{/* Fullscreen Video Background */}
			<div className="fixed inset-0 z-0">
				<VideoCarousel />
			</div>

			{/* Navigation */}
			<nav className="glass-dark fixed top-0 left-0 right-0 z-50 border-b border-white/10">
				<div className="container mx-auto px-4 py-4 flex justify-between items-center">
					<Link href="/" className="flex items-center">
						<Image
							src="/ts-logo2-v2.png"
							alt="TabStudio Logo"
							width={160}
							height={53}
							className="h-12 w-auto"
							loading="lazy"
							unoptimized
						/>
					</Link>
					<div className="flex items-center gap-4">
						<Link href="/login" prefetch={false}>
							<Button
								variant="ghost"
								size="sm"
								className="glass-light glass-hover text-white border-white/20"
							>
								Sign In
							</Button>
						</Link>
						<Link href="/register" prefetch={false}>
							<Button
								variant="default"
								size="sm"
								className="glass-frosted glass-hover bg-white/90 text-black font-semibold border-white/30"
							>
								Get Started
							</Button>
						</Link>
					</div>
				</div>
			</nav>

			<main className="relative z-10 pt-24">
				{/* Hero Section */}
				<section className="py-24 px-4">
					<div className="container mx-auto text-center">
						<h1 className="text-5xl md:text-7xl font-bold mb-6">
							Security at
							<br />
							<span className="bg-gradient-to-r from-red-500 to-red-700 bg-clip-text text-transparent">
								TabStudio
							</span>
						</h1>
						<p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
							Your data security and privacy are our top
							priorities. Learn about our security practices and
							certifications.
						</p>
					</div>
				</section>

				{/* Security Features */}
				<section className="py-16 px-4 bg-card/30 backdrop-blur-sm">
					<div className="container mx-auto">
						<h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
							Security Features
						</h2>
						<div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-primary to-primary-dark rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									End-to-End Encryption
								</h3>
								<p className="text-muted-foreground">
									All data transmitted between your device and
									our servers is encrypted using TLS 1.3 with
									256-bit AES encryption.
								</p>
							</div>

							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-secondary to-secondary-dark rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									SOC 2 Type II Compliant
								</h3>
								<p className="text-muted-foreground">
									We maintain SOC 2 Type II compliance,
									demonstrating our commitment to security,
									availability, and confidentiality.
								</p>
							</div>

							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-accent to-accent-dark rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									Secure Infrastructure
								</h3>
								<p className="text-muted-foreground">
									Hosted on enterprise-grade cloud
									infrastructure with 99.9% uptime SLA and
									automated backups every 24 hours.
								</p>
							</div>

							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-primary-dark to-secondary rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									Access Controls
								</h3>
								<p className="text-muted-foreground">
									Multi-factor authentication, role-based
									access control, and single sign-on (SSO) for
									Enterprise customers.
								</p>
							</div>

							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-secondary-dark to-accent rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									Regular Audits
								</h3>
								<p className="text-muted-foreground">
									Independent security audits and penetration
									testing performed quarterly by certified
									security firms.
								</p>
							</div>

							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-accent to-primary rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									Incident Response
								</h3>
								<p className="text-muted-foreground">
									24/7 security monitoring with automated
									threat detection and a dedicated incident
									response team.
								</p>
							</div>
						</div>
					</div>
				</section>

				{/* Compliance */}
				<section className="py-16 px-4">
					<div className="container mx-auto max-w-4xl">
						<h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
							Compliance & Certifications
						</h2>
						<div className="grid gap-6 md:grid-cols-2">
							<div className="p-6 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<h3 className="text-xl font-semibold mb-3">
									GDPR
								</h3>
								<p className="text-muted-foreground">
									Fully compliant with the General Data
									Protection Regulation (GDPR) for protecting
									EU citizen data.
								</p>
							</div>

							<div className="p-6 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<h3 className="text-xl font-semibold mb-3">
									CCPA
								</h3>
								<p className="text-muted-foreground">
									Compliant with the California Consumer
									Privacy Act (CCPA) for California residents.
								</p>
							</div>

							<div className="p-6 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<h3 className="text-xl font-semibold mb-3">
									ISO 27001
								</h3>
								<p className="text-muted-foreground">
									Certified for information security
									management systems (ISMS) under ISO/IEC
									27001:2013.
								</p>
							</div>

							<div className="p-6 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<h3 className="text-xl font-semibold mb-3">
									PCI DSS
								</h3>
								<p className="text-muted-foreground">
									Payment Card Industry Data Security Standard
									compliant for secure payment processing.
								</p>
							</div>
						</div>
					</div>
				</section>

				{/* Security Practices */}
				<section className="py-16 px-4 bg-card/30 backdrop-blur-sm">
					<div className="container mx-auto max-w-4xl">
						<h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
							Security Best Practices
						</h2>
						<div className="space-y-6">
							<div className="p-6 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<h3 className="text-xl font-semibold mb-3">
									Data Encryption
								</h3>
								<p className="text-muted-foreground">
									All data is encrypted at rest using AES-256
									encryption and in transit using TLS 1.3.
									Encryption keys are managed using hardware
									security modules (HSMs).
								</p>
							</div>

							<div className="p-6 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<h3 className="text-xl font-semibold mb-3">
									Employee Access
								</h3>
								<p className="text-muted-foreground">
									Strict access controls ensure employees only
									have access to data necessary for their
									roles. All access is logged and monitored.
								</p>
							</div>

							<div className="p-6 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<h3 className="text-xl font-semibold mb-3">
									Network Security
								</h3>
								<p className="text-muted-foreground">
									Our infrastructure is protected by multiple
									layers of firewalls, intrusion detection
									systems, and DDoS protection.
								</p>
							</div>

							<div className="p-6 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<h3 className="text-xl font-semibold mb-3">
									Vulnerability Management
								</h3>
								<p className="text-muted-foreground">
									We maintain a comprehensive vulnerability
									management program with regular scanning,
									patching, and remediation.
								</p>
							</div>
						</div>
					</div>
				</section>

				{/* Responsible Disclosure */}
				<section className="py-16 px-4">
					<div className="container mx-auto max-w-4xl">
						<h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
							Responsible Disclosure
						</h2>
						<div className="glass-gradient p-8 rounded-2xl border border-white/20">
							<p className="text-lg text-muted-foreground mb-6">
								We value the security research community and
								welcome reports of potential security
								vulnerabilities. If you believe you&apos;ve
								found a security issue in our service, please
								report it responsibly.
							</p>
							<div className="mb-6">
								<h3 className="text-xl font-semibold mb-3">
									How to Report
								</h3>
								<p className="text-muted-foreground mb-4">
									Email your findings to{' '}
									<a
										href="mailto:security@tabspace.ai"
										className="text-primary hover:underline"
									>
										security@tabspace.ai
									</a>
									. Please include:
								</p>
								<ul className="list-disc list-inside text-muted-foreground space-y-2">
									<li>Description of the vulnerability</li>
									<li>Steps to reproduce the issue</li>
									<li>Potential impact assessment</li>
									<li>Any proof-of-concept code</li>
								</ul>
							</div>
							<div>
								<h3 className="text-xl font-semibold mb-3">
									What to Expect
								</h3>
								<ul className="list-disc list-inside text-muted-foreground space-y-2">
									<li>
										We will acknowledge receipt of your
										report within 24 hours
									</li>
									<li>
										We will provide an initial assessment
										within 5 business days
									</li>
									<li>
										We will keep you updated on our progress
									</li>
									<li>
										We will credit you in our security hall
										of fame (if desired)
									</li>
								</ul>
							</div>
						</div>
					</div>
				</section>

				{/* CTA Section */}
				<section className="py-24 px-4 bg-card/30 backdrop-blur-sm">
					<div className="container mx-auto">
						<div className="glass-gradient glass-hover relative max-w-4xl mx-auto text-center p-12 rounded-3xl border border-white/20 overflow-hidden shadow-2xl">
							<div className="relative z-10">
								<h2 className="text-4xl md:text-5xl font-bold mb-6">
									Questions About Security?
								</h2>
								<p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
									Our security team is here to address your
									concerns and provide detailed information.
								</p>
								<div className="flex gap-4 justify-center flex-col sm:flex-row">
									<a href="mailto:security@stabspace.ai">
										<Button
											variant="default"
											size="lg"
											className="glass-frosted glass-hover bg-white/90 text-black font-semibold border-white/30"
										>
											Contact Security Team
										</Button>
									</a>
									<Link href="/privacy" prefetch={false}>
										<Button
											variant="outline"
											size="lg"
											className="glass-light glass-hover border-2 border-white/30 text-white font-semibold"
										>
											Privacy Policy
										</Button>
									</Link>
								</div>
							</div>
						</div>
					</div>
				</section>
			</main>

			{/* Footer */}
			<footer className="border-t border-border/50 bg-card/30 backdrop-blur-sm">
				<div className="container mx-auto px-4 py-12">
					<div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
						<div>
							<h4 className="font-semibold mb-4">Product</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/features">Features</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Company</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/about">About</Link>
								</li>
								<li>
									<Link href="/careers">Careers</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Resources</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/help">Help Center</Link>
								</li>
								<li>
									<Link href="/api">API</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Legal</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/privacy">Privacy</Link>
								</li>
								<li>
									<Link href="/terms">Terms</Link>
								</li>
								<li>
									<Link href="/security">Security</Link>
								</li>
							</ul>
						</div>
					</div>
					<div className="pt-8 border-t border-border/50 text-center text-sm text-muted-foreground">
						<p>
							&copy; 2025 TabStudio. Powered by AI. Built for
							creators.
						</p>
					</div>
				</div>
			</footer>
		</div>
	);
}
